<script setup>
import { VuePDF } from '@tato30/vue-pdf'
import { usePDF } from './pdf.js'
import { useEventListener, useParentElement } from '@vueuse/core'
import { toRef, useTemplateRef, ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { scrollTo } from '@/lib/dom'

const emit = defineEmits(['loaded', 'page'])
const props = defineProps({
  link: {
    type: [String, Uint8Array, ArrayBuffer, Object],
    default: null,
  },
  scale: { type: Number, default: 1 },
  /** Если задано — рендерим страницы 1…maxPage (например, без листа «Приложения») */
  maxPage: {
    type: Number,
    default: null,
  },
})

/**
 * @type {IntersectionObserver} observer
 */
let observer = null
let pageLoaded = 0
let intersecting = true
const pageCurrent = ref(1)
const dnLink = toRef(props, 'link')
const pagesCanvaEl = useTemplateRef('pages-canva')
const pageEl = useTemplateRef('page-el')
const scrollRoot = useParentElement()
const { pdf, print, pages } = usePDF(dnLink)

const pageCount = computed(() => {
  const n = Number(pages.value)
  return Number.isFinite(n) && n > 0 ? n : 0
})

/** usePDF.pages — numPages (число), не массив. */
const renderedPages = computed(() => {
  const all = Array.from({ length: pageCount.value }, (_, i) => i + 1)
  const cap = props.maxPage
  if (cap != null && Number.isFinite(cap) && cap > 0) {
    return all.filter(p => p <= cap)
  }
  return all
})

defineExpose({ pages, jumpTo, print })

let pageLoadFallbackTimer = null

onMounted(() => {
  observerInit()
})
onBeforeUnmount(() => {
  // Сразу выкинуть холсты и текстовые слои одним снятием, не по span на каждую страницу.
  pagesCanvaEl.value?.replaceChildren()
  observer?.disconnect?.()
  observer = null
  if (pageLoadFallbackTimer) {
    clearTimeout(pageLoadFallbackTimer)
    pageLoadFallbackTimer = null
  }
})

watch(pageCount, n => {
  if (pageLoadFallbackTimer) {
    clearTimeout(pageLoadFallbackTimer)
    pageLoadFallbackTimer = null
  }
  if (n <= 0) return
  pageLoadFallbackTimer = setTimeout(() => {
    pageLoadFallbackTimer = null
    const need = renderedPages.value.length
    if (pageLoaded < need || (pageCount.value <= 0 && props.link)) {
      emit('loaded', Math.max(need, pageCount.value, 1), props.scale)
      pageLoaded = 0
    }
  }, 5000)
})

function observerInit() {
  if (observer) {
    observer.disconnect()
    observer = null
  }
  observer = new IntersectionObserver(onIntersect, { threshold: 0.08, root: scrollRoot.value })
}
function onIntersect(entries) {
  if (!intersecting) {
    return
  }
  for (let entry of entries) {
    if (entry.isIntersecting) {
      const i = Array.prototype.indexOf.call(pagesCanvaEl.value.children, entry.target)
      pageCurrent.value = i + 1
      emit('page', pageCurrent.value, renderedPages.value.length || pages.value)
    }
  }
}
function onPageLoaded(e) {
  pageLoaded++
  const need = renderedPages.value.length
  if (pageLoaded == need && need > 0) {
    emit('loaded', need, e.scale)
    pageLoaded = 0
    observerInit()
    for (let p of pageEl.value || []) {
      observer.observe(p)
    }
  }
}

/**
 * pdf.js растягивает невидимый .endOfContent на страницу только с классом
 * selecting — иначе выделение обрывается в промежутках между строками.
 *
 * @param {PointerEvent} event
 */
function onTextPointerDown(event) {
  const layer = event.target?.closest?.('.textLayer')
  if (!layer) {
    return
  }
  layer.classList.add('selecting')
}

/** Снимает режим выделения после отпускания указателя. */
function clearTextSelecting() {
  const root = pagesCanvaEl.value
  if (!root) {
    return
  }
  root.querySelectorAll('.textLayer.selecting').forEach(layer => {
    layer.classList.remove('selecting')
  })
}

useEventListener(window, 'pointerup', clearTextSelecting)
useEventListener(window, 'pointercancel', clearTextSelecting)

function jumpTo(n) {
  let pg = pageEl.value?.[n - 1]
  if (pg == undefined) {
    return
  }
  intersecting = false
  scrollTo(scrollRoot.value, pg.offsetTop, () => (intersecting = true))
}
</script>

<template>
  <div class="bg-light p-3" @pointerdown="onTextPointerDown">
    <div class="vstack gap-3 docview-pdf" ref="pages-canva">
      <div
        class="pdf-view border align-self-center shadow"
        v-for="p of renderedPages"
        :key="p"
        ref="page-el"
      >
        <!-- text-layer: прозрачные глифы поверх холста, только выделение и Ctrl+C -->
        <VuePDF
          :pdf="pdf"
          :page="p"
          :scale="props.scale"
          text-layer
          @loaded="onPageLoaded"
        />
      </div>
    </div>
  </div>
</template>
